<template>
  <div class="w-100 d-flex flex-column justify-content-between charts-infographic" ref="customChartInfoRef">
    <div class="extra-chart-infographic-top">
      {{ t('bi.All') }} <span>{{ total.toLocaleString() }}</span> {{ suffix }}
    </div>
    <template v-if="items.length > 0">
      <div class="chart-infographic-content">
        <template v-for="(item, index) in items" :key="item.type">
          <div class="chart-infographic-content-item" v-if="showItems[index]">
            <component
              class="infographic-kits"
              :id="'infoSvgComponentRef-' + infographicUniqueId + '-' + item.type"
              :is="resolveKits(item.type)"
              :style="{ fill: item.itemStyle.color, width: autoRatioWidthHandler(75, '150px'), height: autoRatioHeightHandler(75, '150px') }"
              @mousemove="infoElements($event, item.type, item.itemStyle.color)"
              @dblclick="isShowInfoAble ? $emit('on-click-data-info') : ''"
            />
            <div
              class="chart-infographic-content-item-title"
              :style="{
                fontSize: autoRatioHeightHandler(400, '1.5rem'),
                color: item.itemStyle.color,
                marginTop: autoRatioHeightHandler(400, '1rem'),
              }"
            >
              {{ `${item.percent}%` }}
            </div>
            <div
              class="chart-infographic-content-item-desc"
              :style="{ fontSize: autoRatioHeightHandler(450, '1.07rem'), marginTop: autoRatioHeightHandler(3000, '0rem') }"
            >
              {{ item.value.toLocaleString() + ` ${item.suffix}` }}
            </div>
            <span
              :id="'tooltip-' + infographicUniqueId + '-' + item.type"
              class="tooltip-content-hover font-f3"
              :style="{ border: '1px solid ' + item.itemStyle.color }"
            >
              <div>
                <div>{{ item.name }}</div>
                <div class="tooltip-value font-f2">
                  <div class="tooltips-point" :style="'background-color:' + item.itemStyle.color" />
                  {{ item.value.toLocaleString() }} <span>{{ item.suffix }}</span>
                </div>
              </div>
            </span>
          </div>
        </template>
      </div>
      <div class="chart-infographic-legend">
        <template v-for="(item, index) in items" :key="item.type">
          <div
            class="chart-infographic-legend-item"
            :class="[showItems[index] ? '' : 'disable']"
            @click="onClickLegend(index)"
            @mouseenter="onMouseEnterLegend(item.type, item.itemStyle.color)"
            @mouseleave="onMouseLeaveLegend(item.type)"
          >
            <div class="chart-infographic-legend-box" :style="'background-color:' + item.itemStyle.color"></div>
            {{ item.name }}
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="empty-chart-icon-container">
        <empty-chart-icon />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, Ref, defineEmits, getCurrentInstance } from 'vue';

import EmptyChartIcon from '@/assets/icons/modules/analytics/bi/EmptyChartIcon.vue';

import FemaleKit from '@/assets/icons/modules/analytics/dashboard/FemaleKit.vue';
import MaleKit from '@/assets/icons/modules/analytics/dashboard/MaleKit.vue';

// Infographic new
import Childern from '@/assets/icons/modules/analytics/dashboard/Child.vue';
import Teenager from '@/assets/icons/modules/analytics/dashboard/Teen.vue';
import YoungAdult from '@/assets/icons/modules/analytics/dashboard/YoungAdult.vue';
import EarlyCareer from '@/assets/icons/modules/analytics/dashboard/EarlyCareer.vue';
import MidCareer from '@/assets/icons/modules/analytics/dashboard/MidCareer.vue';
import MatureAdult from '@/assets/icons/modules/analytics/dashboard/MatureAdult.vue';
import Senior from '@/assets/icons/modules/analytics/dashboard/Senior.vue';
// Infographic old
import Child from '@/assets/icons/modules/analytics/dashboard/Child.vue';
import Teen from '@/assets/icons/modules/analytics/dashboard/Teen.vue';
import LateTeenKit from '@/assets/icons/modules/analytics/dashboard/LateTeenKit.vue';
import WorkingKit from '@/assets/icons/modules/analytics/dashboard/WorkingKit.vue';
import OldKit from '@/assets/icons/modules/analytics/dashboard/OldKit.vue';
import NonSpecified from '@/assets/icons/modules/analytics/dashboard/NonSpecified.vue';

import { GENDER_AND_AGE } from '@/constants/components/chart/chart-infographic';

import { useElementSize } from '@vueuse/core';

import { useI18n } from 'vue-i18n';

interface Props {
  rawData: Charts.ChartsProps.RawData;
  items: Infographic.Item[];
  suffix: string;
  autoRatio?: boolean;
  isShowInfoAble?: boolean;
  isShowExtraTopNoData?: boolean;
}

const infographicUniqueId: Ref<string> = ref(Math.random().toFixed(8).replace('.', ''));

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  suffix: 'unit',
  autoRatio: false,
  isShowInfoAble: false,
  isShowExtraTopNoData: true,
});

const { t } = useI18n();

defineEmits(['on-click-data-info']);

//const vm = getCurrentInstance()?.proxy;

let showItems: Ref<boolean[]> = ref([]);

const resolveKits = (kit: string) => {
  switch (kit) {
    case GENDER_AND_AGE.FEMALE:
      return FemaleKit;
    case GENDER_AND_AGE.MALE:
      return MaleKit;
    case GENDER_AND_AGE.NOT_SPECIFIED:
      return NonSpecified;
    // Infographic new
    case GENDER_AND_AGE.CHILDREN:
      return Childern;
    case GENDER_AND_AGE.TEENAGER:
      return Teenager;
    case GENDER_AND_AGE.YOUNG_ADULT:
      return YoungAdult;
    case GENDER_AND_AGE.EARLY_CAREER:
      return EarlyCareer;
    case GENDER_AND_AGE.MID_CAREER:
      return MidCareer;
    case GENDER_AND_AGE.MATURE_ADULT:
      return MatureAdult;
    case GENDER_AND_AGE.SENIOR:
      return Senior;
    // Infographic old
    case GENDER_AND_AGE.CHILD:
      return Child;
    case GENDER_AND_AGE.TEEN:
      return Teen;
    case GENDER_AND_AGE.LATE_TEEN:
      return LateTeenKit;
    case GENDER_AND_AGE.WORKING:
      return WorkingKit;
    case GENDER_AND_AGE.OLD:
      return OldKit;
    default:
      return NonSpecified;
  }
};

const customChartInfoRef = ref(null);
const { width, height } = useElementSize(customChartInfoRef);

const autoRatioWidthHandler = computed(() => {
  return (divider: number, defaultValue: string) => `${props.autoRatio ? String(width.value / divider) + 'rem' : defaultValue}`;
});

const autoRatioHeightHandler = computed(() => {
  return (divider: number, defaultValue: string) => `${props.autoRatio ? String(height.value / divider) + 'rem' : defaultValue}`;
});

const total = computed<number>(() => {
  const initialValue = 0;
  const nullData = calculateNoDataValue();
  if (props.items.length > 0) {
    return props.items.reduce((previousValue, currentValue) => previousValue + currentValue.value, initialValue + nullData);
  } else {
    return initialValue;
  }
});

function calculateNoDataValue() {
  if (props.rawData && props.rawData.data) {
    const nullData = props.rawData.data.find((item: any) => item.type === GENDER_AND_AGE.NULL_DATA);
    return nullData !== undefined ? nullData.value : 0;
  } else {
    return 0;
  }
}

// Get mouse position in screen to use to declare position for tooltip
const getMousePos = (evt: MouseEvent) => {
  const pos = (evt.currentTarget as HTMLElement).getBoundingClientRect();
  const posX = evt.clientX - pos.left;
  const posY = evt.clientY - pos.top;
  if (posX - pos.width > -(pos.width / 2)) {
    // pos.width is currentWidth of svg
    // Set tooltip position to left or right on SVG element
    return {
      x: posX - (pos.width + 10),
      y: posY - pos.width / 2 + 100,
    };
  } else {
    return {
      x: posX,
      y: posY - pos.width / 2 + 100,
    };
  }
};

const infoElements = (el: MouseEvent, type: string, color: string) => {
  const mPos = getMousePos(el);
  setupTooltip(mPos.x, mPos.y, type, color);
};

const setupTooltip = (posX: number, posY: number, type: string, color: string) => {
  const tooltip = document.querySelector('#tooltip-' + infographicUniqueId.value + '-' + type) as HTMLElement;
  if (tooltip) {
    tooltip.setAttribute('style', `left:${posX}px;top:${posY}px;border:1px solid ${color}`);
  }
};

// Display infographic element when legend click
const onClickLegend = (index: number) => {
  showItems.value[index] = !showItems.value[index];
};

//Set position of legend to center svg element
const onMouseEnterLegend = (type: string, color: string) => {
  const el = document.querySelector('#infoSvgComponentRef-' + infographicUniqueId.value + '-' + type) as HTMLElement;
  if (el) {
    el.classList.add('hover');
    const elPos = el.getBoundingClientRect();
    const tooltipEl = document.querySelector('#tooltip-' + infographicUniqueId.value + '-' + type) as HTMLElement;
    if (tooltipEl) {
      const tooltipPos = tooltipEl.getBoundingClientRect();
      const x = elPos.x - elPos.left + elPos.width / 2 - tooltipPos.width / 2;
      const y = elPos.y - elPos.top + elPos.height / 3;
      setupTooltip(x, y, type, color);
    }
  }
};

const onMouseLeaveLegend = (type: string) => {
  const el = document.querySelector('#infoSvgComponentRef-' + infographicUniqueId.value + '-' + type) as HTMLElement;
  if (el) {
    el.classList.remove('hover');
  }
};

onMounted(() => {
  //Get display array of infographic items
  for (let i = 0; i < props.items.length; i++) {
    showItems.value[i] = true;
  }
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/modules/analytics/bi/analytics-bi-charts-infographic.scss';
</style>
